import * as React from 'react';
import * as moment from 'moment';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, MicrositeActionCreators, IMicrositeShortCodeResponse, IMicrositeCampaignResponse, IMicrositeCampaignLookupDataResponse } from '@nkr/store/index';
import { ICenterBase } from '@nkr/models/center-base';
import { IListItem } from '@nkr/models/list-item';
import { CampaignType, MicrositeType, ShortCodeType } from '@nkr/enums/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Glyphicon, HelpBlock, InputGroup, Panel, Row, Well } from 'react-bootstrap';
import { generateQRCodePngDataURL } from './letter/campaign-document';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { DisabledFormControl, FormWrapper } from '../styles';
import MaskedInput from 'react-maskedinput';
import CampaignSteps from './campaign-steps';

interface IProps {
    campaignId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCampaign: (id: number) => Promise<IMicrositeCampaignResponse>,
    getShortCode: (type: ShortCodeType) => Promise<IMicrositeShortCodeResponse>,
    getLookupData: () => Promise<IMicrositeCampaignLookupDataResponse>,
    saveCampaign: (
        id: number,
        centerId: number,
        selfRegistrationKey: string,
        campaignName: string,
        micrositeTypeId: number,
        startDate: string,
        endDate: string,
        campaignTypeId: number,
        processedByNkr: boolean,
        letter: any
    ) => Promise<IMicrositeCampaignResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IValidation {
    center: boolean,
    selfRegistrationKey: boolean,
    confirmSelfRegistrationKey: boolean,
    campaignName: boolean,
    micrositeTypeId: boolean,
    startDate: boolean,
    endDate: { status: number, message: string },
}

interface IState {
    centerId: number,
    selfRegistrationKey: string,
    startDate: string,
    endDate: string,
    campaignName: string,
    campaignTypeId: number,
    micrositeTypeId: number,
    processedByNkr: boolean,
    confirmSelfRegistrationKey: boolean,
    showPrintingMailing: boolean,
    qrCode: string | null,
    validation: IValidation,
    dirty: boolean,
    centerList: ICenterBase[],
    micrositeTypeList: IListItem[]
}

class CampaignAddEdit extends React.Component<TProps, IState> {
    
    readonly state: IState = {
        centerId: this.props.centerId,
        selfRegistrationKey: null,
        startDate: moment().format('MM/DD/YYYY'),
        endDate: '',
        campaignName: '',
        campaignTypeId: CampaignType.InHouse,
        micrositeTypeId: MicrositeType.Kidney,
        processedByNkr: false,
        confirmSelfRegistrationKey: false,
        showPrintingMailing: false,
        qrCode: null,
        validation: {
            center: true,
            selfRegistrationKey: true,
            confirmSelfRegistrationKey: true,
            campaignName: true,
            micrositeTypeId: true,
            startDate: true,
            endDate: { status: 1, message: '' },
        },
        dirty: false,
        centerList: [],
        micrositeTypeList: []
    }

    componentDidMount = () => {
        if (this.props.campaignId > 0) {
            this.props.showLoading('Getting the campaign, one moment please...').then(() => {
                Promise.all([
                    this.props.getCampaign(this.props.campaignId),
                    this.props.getLookupData()
                ])
                    .then(async ([response, lookupDataResponse]) => {
                        if (response.data.success && lookupDataResponse.data.success) {
                            let { centerId, selfRegistrationKey, startDate, endDate, campaignName, campaignTypeId, micrositeTypeId, processedByNkr } = response.data.results;

                            const qrCode = await generateQRCodePngDataURL(`${__NKR_ORG_URL__}/register/${selfRegistrationKey}`);

                            let { centerList, micrositeTypeList } = lookupDataResponse.data.results;

                            this.setState({
                                centerId, selfRegistrationKey, startDate, endDate, campaignName, campaignTypeId, processedByNkr,
                                micrositeTypeId,
                                confirmSelfRegistrationKey: true,
                                showPrintingMailing: response.data.results.campaignTypeId == CampaignType.MailedOut,
                                centerList,
                                micrositeTypeList,
                                qrCode
                            });
                        }
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        } else {
            Promise.all([
                this.props.getLookupData(),
                this.props.getShortCode(ShortCodeType.Campaign)
            ])
                .then(async ([lookupDataResponse, shortCodeResponse]) => {
                    if (lookupDataResponse.data.success && shortCodeResponse.data.success) {
                        const qrCode = await generateQRCodePngDataURL(`${__NKR_ORG_URL__}/register/${shortCodeResponse.data.results}`);

                        let { centerList, micrositeTypeList } = lookupDataResponse.data.results;

                        this.setState({
                            selfRegistrationKey: shortCodeResponse.data.results,
                            centerList,
                            micrositeTypeList,
                            qrCode
                        });
                    }
                });
        }
    }

    handleRegenerateKey = () => {
        this.getSelfRegistrationKey();
    }

    handleSetDate = () => {
        this.setState({
            startDate: moment().format('MM/DD/YYYY')
        });
    }

    handleFieldChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement & HTMLInputElement>) => {
        let newState = _.cloneDeep(this.state);
        let { name, value } = e.currentTarget;

        if (name == 'centerId') {
            newState.centerId = parseInt(value);

            if (parseInt(value) == -1) {
                newState.campaignTypeId = CampaignType.InHouse;
            }
        }

        if (name == 'confirmSelfRegistrationKey') {
            newState.confirmSelfRegistrationKey = e.currentTarget.checked;
        }

        if (name == 'campaignName') {
            newState.campaignName = value;
        }

        if (name == 'startDate') {
            newState.startDate = value;
        }

        if (name == 'endDate') {
            newState.endDate = value;
        }

        if (name == 'campaignType') {
            newState.campaignTypeId = parseInt(value);
        }

        if (name == 'micrositeTypeId') {
            newState.micrositeTypeId = parseInt(value);
        }

        this.setState(newState);
    }

    handleSave = (goNextStep = false) => {
        let validation = this.validate();

        let baseUrl = this.props.isAdmin ? '/admin/microsite/campaign' : '/center/' + this.props.centerId + '/microsite/campaign';

        this.setState({
            validation,
            dirty: true
        }, () => {
            if (this.isValid()) {
                let { centerId, selfRegistrationKey, campaignName, micrositeTypeId, startDate, endDate, campaignTypeId, processedByNkr } = this.state;

                this.props.showLoading('Saving campaign, one moment please...').then(() => {
                    this.props.saveCampaign(this.props.campaignId, centerId, selfRegistrationKey, campaignName, micrositeTypeId, startDate, endDate, campaignTypeId, processedByNkr, null)
                        .then(response => {
                            toast.success(`Campaign saved successfully, going to ${goNextStep ? 'upload mailing list' : 'the campaign list'} ...`, {
                                onClose: () => {
                                    window.location.href = goNextStep ? `${baseUrl}/${response.data.results.id}/mailing-list` : this.getReturnUrl();
                                }
                            });
                        })
                        .catch((error: AxiosError) => {
                            toast.error('Unable to save campaign, we encountered the following error: ' + error.message);
                        })
                        .finally(() => {
                            this.props.hideLoading();
                        });
                });
            }
        });
    }

    handleCancel = () => {
        let href = this.getReturnUrl();
        window.location.href = href;
    }

    getReturnUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaigns` : `/center/${this.props.centerId}/microsite/campaigns`;
        return href;
    }

    getSelfRegistrationKey = () => {
        this.props.getShortCode(ShortCodeType.Campaign).then(async response => {
            if (response.data.success) {
                let qrCode = await generateQRCodePngDataURL(`${__NKR_ORG_URL__}/register/${response.data.results}`);

                this.setState({
                    selfRegistrationKey: response.data.results,
                    qrCode
                });
            }
        });
    }

    validate = () => {
        let { centerId, selfRegistrationKey, campaignName, micrositeTypeId, startDate, endDate } = this.state;

        let centerValid = centerId == -1 || centerId > 0;
        let selfRegistrationKeyValid = selfRegistrationKey != '';
        let confirmSelfRegistrationKeyValid = this.state.confirmSelfRegistrationKey;
        let campaignNameValid = campaignName != '';
        let micrositeTypeIdValid = micrositeTypeId >= 0;
        let startDateValid = startDate != '';
        let endDateValid = endDate != '' ? 1 : 2;
        let endDateMessage = endDateValid == 1 ? '' : 'The end date is required';

        if (startDateValid && endDateValid) {
            let sd = moment(startDate);
            let ed = moment(endDate);

            if (ed.isBefore(sd)) {
                endDateValid = 3;
                endDateMessage = 'The end date must occur after the start date';
            }
        }

        let validation: IValidation = {
            center: centerValid,
            selfRegistrationKey: selfRegistrationKeyValid,
            confirmSelfRegistrationKey: confirmSelfRegistrationKeyValid,
            campaignName: campaignNameValid,
            micrositeTypeId: micrositeTypeIdValid,
            startDate: startDateValid,
            endDate: { status: endDateValid, message: endDateMessage }
        };

        return validation;
    }

    isValid = () => {
        let { center, selfRegistrationKey, confirmSelfRegistrationKey, campaignName, micrositeTypeId, startDate, endDate } = this.state.validation;

        return center
            && selfRegistrationKey
            && confirmSelfRegistrationKey
            && campaignName
            && micrositeTypeId
            && startDate
            && endDate.status == 1;
    }

    render = () => {
        const title = this.props.campaignId != 0 ? 'Edit Campaign' : 'Add Campaign';
        const isValid = this.isValid();

        const centerList = _.sortBy(this.state.centerList, item => item.alias);

        const isMailedOut = this.state.campaignTypeId == CampaignType.MailedOut;

        return (
            <>
                {!isValid && (
                    <Row>
                        <Col xs={12} md={8} mdOffset={2}>
                            <Alert bsStyle='danger' bsSize='sm'>
                                We can't accept the campaign until the items outlined in red are resolved...
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col xs={12} md={8} mdOffset={2}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 25 }}>
                            <h1 className='text-left' style={{ margin: 0 }}>{title}</h1>
                            <div className='dropdown pull-right'>
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to Campaign List</Button>
                            </div>
                        </div>

                        <Panel>
                            <Panel.Body>
                                <FormWrapper horizontal>
                                    {this.props.isAdmin && (
                                        <FormGroup validationState={this.state.dirty && !this.state.validation.center ? 'error' : null}>
                                            <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='centerId'> Center:</Col>
                                            <Col xs={12} sm={8}>
                                                <FormControl
                                                    componentClass='select'
                                                    name='centerId'
                                                    value={this.state.centerId}
                                                    onChange={this.handleFieldChange}
                                                >
                                                    <option value={0}>- Please Select -</option>
                                                    <option value={-1}>- NO CENTER -</option>
                                                    {centerList.map((item, index) =>
                                                        <option key={'ctr-' + index} value={item.id}>{item.alias}</option>
                                                    )}
                                                </FormControl>

                                                {this.state.dirty && !this.state.validation.center && (
                                                    <HelpBlock>Please select a center or <strong>- NO CENTER -</strong></HelpBlock>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup validationState={this.state.dirty && !this.state.validation.selfRegistrationKey ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Computer Generated Registration Key:</Col>
                                        <Col xs={12} sm={8}>
                                            {this.props.campaignId == 0 ?
                                                <InputGroup>
                                                    <FormControl
                                                        maxLength={6}
                                                        value={this.state.selfRegistrationKey}
                                                    />
                                                    <InputGroup.Button>
                                                        <Button onClick={this.handleRegenerateKey}>
                                                            <span className='glyphicon glyphicon-refresh'></span> Regenerate
                                                        </Button>
                                                    </InputGroup.Button>
                                                </InputGroup>
                                                :
                                                <DisabledFormControl className='form-control'>{this.state.selfRegistrationKey}</DisabledFormControl>
                                            }
                                        </Col>
                                    </FormGroup>

                                    {this.props.campaignId == 0 && (
                                        <FormGroup validationState={this.state.dirty && !this.state.validation.confirmSelfRegistrationKey ? 'error' : null}>
                                            <Col sm={4}>&nbsp;</Col>
                                            <Col xs={12} sm={8}>
                                                <Well style={{ marginBottom: 0, borderColor: (this.state.dirty && !this.state.validation.confirmSelfRegistrationKey ? '#a94442' : '#ccc') }}>
                                                    <Row>
                                                        <Col xs={1} sm={1}>
                                                            <input
                                                                type='checkbox'
                                                                className='form-control'
                                                                id='confirmSelfRegistrationKey'
                                                                name='confirmSelfRegistrationKey'
                                                                checked={this.state.confirmSelfRegistrationKey}
                                                                onChange={this.handleFieldChange}
                                                            />
                                                        </Col>
                                                        <Col xs={11} sm={11}>
                                                            <label style={{ fontWeight: 'normal' }} htmlFor='confirmSelfRegistrationKey'>
                                                                I confirm that the above computer-generated short-code doesn't have any text that could be perceived as a
                                                                word. (if it does have something that could be sounded out as a word, please click the Regenerate button to replace it).
                                                            </label>
                                                        </Col>
                                                    </Row>
                                                </Well>

                                                {this.state.dirty && !this.state.validation.confirmSelfRegistrationKey && (
                                                    <HelpBlock>Confirmation is required</HelpBlock>
                                                )}
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}> QR Code:</Col>
                                        <Col xs={12} sm={2}>
                                            {this.state.qrCode && (
                                                <img src={this.state.qrCode} alt='QR Code' style={{ width: '100px', height: '100px' }} />
                                            )}
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <ControlLabel>Microsite Link</ControlLabel>
                                            <div><a href={`${__NKR_ORG_URL__}/register/${this.state.selfRegistrationKey}`} target='_blank' rel='noopener noreferrer'>{`${__NKR_ORG_URL__}/${this.state.selfRegistrationKey}`}</a></div>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={this.state.dirty && !this.state.validation.campaignName ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}> Campaign:</Col>
                                        <Col xs={12} sm={8}>
                                            <FormControl
                                                maxLength={64}
                                                name='campaignName'
                                                value={this.state.campaignName}
                                                onChange={this.handleFieldChange}
                                            />

                                            {this.state.dirty && !this.state.validation.campaignName && (
                                                <HelpBlock>The campaign name is required</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={this.state.dirty && !this.state.validation.micrositeTypeId ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='micrositeTypeId'> Microsite Type:</Col>
                                        <Col xs={12} sm={8}>
                                            <FormControl
                                                id='micrositeTypeId'
                                                componentClass='select'
                                                name='micrositeTypeId'
                                                value={this.state.micrositeTypeId}
                                                onChange={this.handleFieldChange}
                                            >
                                                <option value={-1}>- Please Select -</option>
                                                {this.state.micrositeTypeList.map((item, index) =>
                                                    <option key={'ms-type-' + index} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>

                                            {this.state.dirty && !this.state.validation.micrositeTypeId && (
                                                <HelpBlock>Microsite type is required</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={this.state.dirty && !this.state.validation.startDate ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}> Start Date:</Col>
                                        <Col xs={12} sm={8}>
                                            <InputGroup>
                                                <MaskedInput
                                                    className='form-control'
                                                    mask='11/11/1111'
                                                    placeholder='mm/dd/yyyy'
                                                    style={{ borderColor: this.state.validation.startDate ? '#ccc' : '#a94442' }}
                                                    name='startDate'
                                                    value={this.state.startDate}
                                                    onChange={this.handleFieldChange}
                                                />
                                                <InputGroup.Button>
                                                    <Button onClick={this.handleSetDate}><Glyphicon glyph='calendar' />Today's Date</Button>
                                                </InputGroup.Button>
                                            </InputGroup>

                                            {this.state.dirty && !this.state.validation.startDate && (
                                                <HelpBlock>The start date is required</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={this.state.dirty && this.state.validation.endDate.status > 1 ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}> End Date:</Col>
                                        <Col xs={12} sm={8}>
                                            <MaskedInput
                                                className='form-control'
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                style={{ borderColor: this.state.validation.endDate ? '#ccc' : '#a94442' }}
                                                name='endDate'
                                                value={this.state.endDate}
                                                onChange={this.handleFieldChange}
                                            />

                                            {this.state.dirty && this.state.validation.endDate.status > 1 && (
                                                <HelpBlock>{this.state.validation.endDate.message}</HelpBlock>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    {this.state.centerId != -1 && (
                                        <FormGroup>
                                            <Col xs={12} sm={4} componentClass={ControlLabel}> Campaign Type:</Col>
                                            <Col xs={12} sm={8}>
                                                <label className='radio-inline'>
                                                    <input
                                                        type='radio'
                                                        id='campaignTypeMailedOut'
                                                        name='campaignType'
                                                        value={CampaignType.InHouse}
                                                        checked={this.state.campaignTypeId == CampaignType.InHouse}
                                                        onChange={this.handleFieldChange}
                                                    /> In House
                                                </label>
                                                <label className='radio-inline'>
                                                    <input
                                                        type='radio'
                                                        id='campaignTypeMailedOut'
                                                        name='campaignType'
                                                        value={CampaignType.MailedOut}
                                                        checked={this.state.campaignTypeId == CampaignType.MailedOut}
                                                        onChange={this.handleFieldChange}
                                                    /> Mailed Out
                                                </label>
                                            </Col>
                                        </FormGroup>
                                    )}
                                </FormWrapper>
                            </Panel.Body>
                        </Panel>

                        {isMailedOut && (
                            <Well bsSize='sm'>
                                <CampaignSteps currentStep={0} campaignId={this.props.campaignId} />
                            </Well>
                        )}

                        <FormGroup style={{ marginTop: 40 }}>
                            <Col xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                                <Button bsStyle={isMailedOut ? 'secondary' : 'primary'} className='col-xs-2' style={{ marginRight: 5 }} onClick={() => this.handleSave()}>Save & Close</Button>

                                {isMailedOut && <Button bsStyle='primary' style={{ marginRight: 5 }} onClick={() => this.handleSave(true)}>Save & Upload Mailing List</Button>}

                                <Button bsStyle='link' className='col-xs-2' onClick={this.handleCancel}>Cancel &amp; Return to the Campaign List</Button>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getCampaign: (id) => {
            return dispatch(MicrositeActionCreators.getCampaign(id));
        },
        getShortCode: (type) => {
            return dispatch(MicrositeActionCreators.getShortCode(type));
        },
        getLookupData: () => {
            return dispatch(MicrositeActionCreators.getCampaignLookupData());
        },
        saveCampaign: (id, centerId, selfRegistrationKey, campaignName, micrositeTypeId, startDate, endDate, campaignTypeId, processedByNkr, letter) => {
            return dispatch(MicrositeActionCreators.saveCampaign(
                id,
                centerId,
                selfRegistrationKey,
                campaignName,
                micrositeTypeId,
                startDate,
                endDate,
                campaignTypeId,
                processedByNkr,
                letter));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CampaignAddEdit);